import miniBook from '@images/printables/mini-book.png'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import doubleSided from '@images/printables/double-sided.png'
import singleSided from '@images/printables/single-sided.png'
import miniBookColor from '@images/printables/mini-book-colour.png'
import doubleSidedColor from '@images/printables/double-sided-colour.png'
import singleSidedColor from '@images/printables/single-sided-colour.png'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import { htmlSafe } from '@ember/template'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { PDFType } from 'teacher-ui-client/components/printables/pdf-types'
import { PDF_TYPES } from 'teacher-ui-client/components/printables/pdf-types'
import { t } from 'ember-intl'
import { eq } from 'ember-truth-helpers'
import type { IntlService } from 'ember-intl'
import type {
  PrintablesLibraryBookEntry,
  PrintablesLibrarySetEntry,
} from 'teacher-ui-client/contentful-client/entry-types'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { pdfDownloadEvent } from 'teacher-ui-client/modifiers/pdf-download-event'
import fullGradeName from 'teacher-ui-client/helpers/full-grade-name'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import { BookPreview } from 'teacher-ui-client/interactive-preview/engines'

interface Signature {
  Args: {
    subscriptionType?: SubscriptionType
    bookSet: PrintablesLibrarySetEntry
    activeBook: PrintablesLibraryBookEntry['fields']
  }
}

interface OptionItem {
  title: string
  url: string
  fileName: string
  pdfType: PDFType
  pdfTypeFormatted: string
  pdfTypeImgUrl: string
}

const pdfTypesAndImages: Record<PDFType, string> = {
  singleSidedPdf: singleSidedColor,
  doubleSidedPdf: doubleSidedColor,
  miniBookPdf: miniBookColor,
  // eslint-disable-next-line customPlugin/prefer-american-color
  colourPdf: singleSidedColor,
  blackAndWhitePdf: singleSided,
  singleBwPdf: singleSided,
  doubleBwPdf: doubleSided,
  miniBookBwPdf: miniBook,
}

export default class ResourceCardBookDetails extends Component<Signature> {
  @service intl!: IntlService

  @service interactivePreview!: InteractivePreviewService

  get hoverColorClass() {
    const isReading = this.args.subscriptionType === SubscriptionType.Reading
    return isReading
      ? 'hover:border-oceany-blue-350 focus:border-oceany-blue-350'
      : 'hover:border-ms-green-350 focus:border-ms-green-350'
  }

  get formattedDescriptionContent() {
    const descriptionContent = this.args.bookSet.fields.description
    return htmlSafe(documentToHtmlString(descriptionContent))
  }

  get availablePdfOptions(): OptionItem[] {
    const { activeBook } = this.args
    const bookTypes = PDF_TYPES
    return bookTypes
      .filter((pdfType) => activeBook[pdfType])
      .map((pdfType) => {
        const pdfFields = activeBook[pdfType]?.fields
        return {
          title: pdfFields?.title ?? '',
          url: pdfFields?.file?.url ?? '',
          fileName: pdfFields?.file?.fileName ?? '',
          pdfType,
          pdfTypeFormatted: this.intl.t(`printables.mediaType.${pdfType}`),
          pdfTypeImgUrl: pdfTypesAndImages[pdfType],
        }
      })
  }

  openBookPreview = (bookCode: string, title: string) => {
    const preview = new BookPreview(this, this.interactivePreview.curriculum, bookCode)
    this.interactivePreview.openPreview(preview, { title })
  }

  <template>
    <div class="flex w-full flex-wrap p-3">
      <div class="w-1/3 text-center">
        <img
          src="{{@activeBook.bookCover.fields.file.url}}?h=250"
          class="border-dusty-black-100 mx-auto mb-3 max-h-72 rounded-md border border-solid p-1 shadow-xs"
          alt={{t "printables.bookCoverPdf"}}
        />
        {{#if @bookSet.fields.programUrl}}
          <button
            type="button"
            {{on "click" (fn this.openBookPreview @bookSet.fields.programUrl @bookSet.fields.bookTitle)}}
          >
            <span class="pr-3 align-middle uppercase">{{t "printables.libraryLink"}}</span>
            <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
          </button>
        {{/if}}
      </div>
      {{! Download section }}
      <div class="w-2/3">
        <div class="text-dusty-black-200 pl-6 font-semibold">{{t "printables.download"}}</div>
        <div class="flex w-full flex-wrap pt-2 pl-6">
          {{#each this.availablePdfOptions as |option|}}
            <div class="mb-1 w-1/3 p-3">
              <a
                href={{option.url}}
                class="block w-full border-2 {{this.hoverColorClass}}"
                target="_blank"
                rel="noreferrer noopener"
                {{pdfDownloadEvent
                  product=@subscriptionType
                  category="Printables Library"
                  label=option.title
                  file=option.fileName
                  pdfType=option.pdfType
                  via="modal"
                }}
              >
                <div class="text-dusty-black-300 pt-2">
                  <div class="w-full text-center text-xs">{{t "printables.pdf"}}</div>
                  <div class="w-full p-2 text-center text-xs">
                    {{option.pdfTypeFormatted}}
                  </div>
                  <img src={{option.pdfTypeImgUrl}} alt={{option.pdfTypeFormatted}} class="mx-auto my-3 block h-12" />
                </div>
              </a>
            </div>
          {{/each}}
        </div>
      </div>

      {{! Details section }}
      <div class="mt-2 flex w-full flex-wrap pl-3">
        <div class="w-1/2">
          <h5 class="mt-1 mb-2">{{@bookSet.fields.bookTitle}}</h5>
          <div class="flex w-full flex-wrap">
            <div class="w-1/2 pb-3">
              <span class="text-dusty-black-200 font-semibold">{{t "printables.details.grade"}}</span>
              <div>{{fullGradeName @bookSet.fields.gradePosition}}</div>
            </div>
            <div class="w-1/2 pb-3">
              <span class="text-dusty-black-200 font-semibold">{{t "printables.details.series"}}</span>
              <div>{{@bookSet.fields.series}}</div>
            </div>
            <div class="w-1/2">
              <span class="text-dusty-black-200 font-semibold">{{t "printables.details.subject"}}</span>
              <div>{{@bookSet.fields.subject}} </div>
            </div>
            <div class="w-1/2">
              <span class="text-dusty-black-200 font-semibold">{{t "printables.details.type"}}</span>
              <div>
                {{#if (eq @activeBook.role "Teacher")}}
                  {{t "printables.teacherBook"}}
                {{else}}
                  {{t "printables.studentBook"}}
                {{/if}}
              </div>
            </div>
          </div>
        </div>
        <div class="w-1/2 pl-2">
          <span class="text-dusty-black-200 font-semibold">{{t "printables.details.description"}}</span>
          <div>
            {{this.formattedDescriptionContent}}
          </div>
        </div>
      </div>
    </div>
  </template>
}
